import { Injectable } from '@angular/core';
import { TourContext } from './tour-context.service';

export type TourKind = 'day-one' | 'manual';
export type OnUnavailable = 'skip' | 'abort';

export interface TourStepDefinition {
  id: string;
  route: string;
  selector: string;
  titleKey: string;
  descriptionKey: string;
  side: 'top' | 'right' | 'bottom' | 'left';
  requires?: (context: TourContext) => boolean;
  openAction?: string;
  closeAction?: string;
  selectorTimeoutMs?: number;
  stabilitySelector?: string;
  onUnavailable: OnUnavailable;
}

const canRead = (permission: string) => (context: TourContext) =>
  context.isAdmin || context.hasPermission(permission);

const DAY_ONE_STEPS: TourStepDefinition[] = [
  {
    id: 'setup-progress', route: '/dashboard', selector: '[data-tour="setup-progress"]',
    titleKey: 'TOUR.DAY_ONE.SETUP.TITLE', descriptionKey: 'TOUR.DAY_ONE.SETUP.DESCRIPTION',
    side: 'bottom', requires: (context) => context.progress < 100, onUnavailable: 'skip'
  },
  {
    id: 'setup-company-profile', route: '/dashboard', selector: '[data-tour="setup-company-profile"]',
    titleKey: 'TOUR.DAY_ONE.COMPANY_PROFILE.TITLE', descriptionKey: 'TOUR.DAY_ONE.COMPANY_PROFILE.DESCRIPTION',
    side: 'right', requires: (context) => context.progress < 100 && !context.companyProfileComplete, onUnavailable: 'skip'
  },
  {
    id: 'setup-digital-configuration', route: '/dashboard', selector: '[data-tour="setup-digital-configuration"]',
    titleKey: 'TOUR.DAY_ONE.DIGITAL_CONFIGURATION.TITLE', descriptionKey: 'TOUR.DAY_ONE.DIGITAL_CONFIGURATION.DESCRIPTION',
    side: 'left', requires: (context) => context.progress < 100 && !context.digitalConfigurationComplete, onUnavailable: 'skip'
  },
  {
    id: 'setup-first-property', route: '/dashboard', selector: '[data-tour="setup-first-property"]',
    titleKey: 'TOUR.DAY_ONE.FIRST_PROPERTY.TITLE', descriptionKey: 'TOUR.DAY_ONE.FIRST_PROPERTY.DESCRIPTION',
    side: 'top', requires: (context) => context.progress < 100 && !context.hasProperty, onUnavailable: 'skip'
  },
  {
    id: 'setup-continue', route: '/dashboard', selector: '[data-tour="setup-continue"]',
    titleKey: 'TOUR.DAY_ONE.CONTINUE.TITLE', descriptionKey: 'TOUR.DAY_ONE.CONTINUE.DESCRIPTION',
    side: 'top', selectorTimeoutMs: 0, requires: (context) => context.progress < 100, onUnavailable: 'skip'
  },
  {
    id: 'global-sidebar', route: '/dashboard', selector: '[data-tour="global-sidebar"]',
    titleKey: 'TOUR.DAY_ONE.NAVIGATION.TITLE', descriptionKey: 'TOUR.DAY_ONE.NAVIGATION.DESCRIPTION',
    side: 'right', requires: canRead('company.dashboard.read'), onUnavailable: 'abort'
  },
  {
    id: 'global-help', route: '/dashboard', selector: '[data-tour="global-help"]',
    titleKey: 'TOUR.DAY_ONE.HELP.TITLE', descriptionKey: 'TOUR.DAY_ONE.HELP.DESCRIPTION',
    side: 'bottom', requires: canRead('company.dashboard.read'), onUnavailable: 'abort'
  }
];

const MANUAL_STEPS: TourStepDefinition[] = [
  {
    id: 'manual-dashboard-overview', route: '/dashboard', selector: '[data-tour="dashboard-overview"]',
    titleKey: 'TOUR.MANUAL.DASHBOARD_OVERVIEW.TITLE', descriptionKey: 'TOUR.MANUAL.DASHBOARD_OVERVIEW.DESCRIPTION',
    side: 'bottom', openAction: 'dashboard.ensure-workspace-ready', closeAction: 'dashboard.ensure-workspace-ready',
    requires: canRead('company.dashboard.read'), onUnavailable: 'skip'
  },
  {
    id: 'manual-dashboard-analytics', route: '/dashboard', selector: '[data-tour="dashboard-analytics"]',
    titleKey: 'TOUR.MANUAL.DASHBOARD_ANALYTICS.TITLE', descriptionKey: 'TOUR.MANUAL.DASHBOARD_ANALYTICS.DESCRIPTION',
    side: 'top', openAction: 'dashboard.ensure-workspace-ready', closeAction: 'dashboard.ensure-workspace-ready',
    requires: canRead('company.dashboard.read'), onUnavailable: 'skip'
  },
  {
    id: 'manual-properties', route: '/properties', selector: '[data-tour="properties-primary-actions"]',
    titleKey: 'TOUR.MANUAL.PROPERTIES.TITLE', descriptionKey: 'TOUR.MANUAL.PROPERTIES.DESCRIPTION',
    side: 'bottom', openAction: 'properties.ensure-primary-actions-ready', closeAction: 'properties.ensure-primary-actions-ready',
    requires: canRead('company.properties.read'), onUnavailable: 'skip'
  },
  {
    id: 'manual-property-actions', route: '/properties', selector: '[data-tour="property-row-actions"]',
    titleKey: 'TOUR.MANUAL.PROPERTY_ACTIONS.TITLE', descriptionKey: 'TOUR.MANUAL.PROPERTY_ACTIONS.DESCRIPTION',
    side: 'left', requires: (context) => canRead('company.properties.read')(context) && context.hasProperty, onUnavailable: 'skip'
  },
  {
    id: 'manual-property-files', route: '/properties', selector: '[data-tour="property-files-modal"]',
    titleKey: 'TOUR.MANUAL.PROPERTY_FILES.TITLE', descriptionKey: 'TOUR.MANUAL.PROPERTY_FILES.DESCRIPTION',
    side: 'top', openAction: 'properties.open-first-files', closeAction: 'properties.open-first-files',
    requires: (context) => canRead('company.property_documents.read')(context) && context.hasProperty, onUnavailable: 'skip'
  },
  {
    id: 'manual-property-instructions', route: '/properties', selector: '[data-tour="property-instructions-content"]',
    titleKey: 'TOUR.MANUAL.PROPERTY_INSTRUCTIONS.TITLE', descriptionKey: 'TOUR.MANUAL.PROPERTY_INSTRUCTIONS.DESCRIPTION',
    side: 'left', openAction: 'properties.open-first-instructions', closeAction: 'properties.open-first-instructions',
    requires: (context) => context.appMode === 'emergency' && canRead('company.properties.read')(context) && context.hasProperty, onUnavailable: 'skip'
  },
  {
    id: 'manual-property-qr', route: '/properties', selector: '[data-tour="property-qr-preview-modal"]',
    titleKey: 'TOUR.MANUAL.PROPERTY_QR.TITLE', descriptionKey: 'TOUR.MANUAL.PROPERTY_QR.DESCRIPTION',
    side: 'top', openAction: 'properties.open-first-qr-preview', closeAction: 'properties.open-first-qr-preview',
    requires: (context) => canRead('company.properties.read')(context) && context.hasProperty, onUnavailable: 'skip'
  },
  {
    id: 'manual-company', route: '/company', selector: '[data-tour="company-general"]',
    titleKey: 'TOUR.MANUAL.COMPANY.TITLE', descriptionKey: 'TOUR.MANUAL.COMPANY.DESCRIPTION',
    side: 'right', stabilitySelector: '[data-tour-ready="company-general"]',
    requires: canRead('company.company_profile.read'), onUnavailable: 'skip'
  },
  {
    id: 'manual-qr-poster', route: '/qr-poster-template', selector: '[data-tour="qr-poster-editor"]',
    titleKey: 'TOUR.MANUAL.QR_POSTER.TITLE', descriptionKey: 'TOUR.MANUAL.QR_POSTER.DESCRIPTION',
    side: 'right', requires: canRead('company.qr_poster_texts.read'), onUnavailable: 'skip'
  },
  {
    id: 'manual-contacts', route: '/contacts', selector: '[data-tour="contacts-overview"]',
    titleKey: 'TOUR.MANUAL.CONTACTS.TITLE', descriptionKey: 'TOUR.MANUAL.CONTACTS.DESCRIPTION',
    side: 'bottom', stabilitySelector: '[data-tour-ready="contacts-overview"]',
    requires: (context) => context.appMode === 'emergency' && canRead('company.contacts.read')(context), onUnavailable: 'skip'
  },
  {
    id: 'manual-announcements', route: '/announcements', selector: '[data-tour="announcements-overview"]',
    titleKey: 'TOUR.MANUAL.ANNOUNCEMENTS.TITLE', descriptionKey: 'TOUR.MANUAL.ANNOUNCEMENTS.DESCRIPTION',
    side: 'bottom', stabilitySelector: '[data-tour-ready="announcements-overview"]',
    requires: canRead('company.announcements.read'), onUnavailable: 'skip'
  },
  {
    id: 'manual-audit', route: '/audit-logs', selector: '[data-tour="audit-logs-overview"]',
    titleKey: 'TOUR.MANUAL.AUDIT.TITLE', descriptionKey: 'TOUR.MANUAL.AUDIT.DESCRIPTION',
    side: 'bottom', stabilitySelector: '[data-tour-ready="audit-logs-overview"]',
    requires: canRead('company.audit_logs.read'), onUnavailable: 'skip'
  },
  {
    id: 'manual-settings', route: '/account/settings/account', selector: '[data-tour="account-settings-overview"]',
    titleKey: 'TOUR.MANUAL.SETTINGS.TITLE', descriptionKey: 'TOUR.MANUAL.SETTINGS.DESCRIPTION',
    side: 'bottom', requires: (context) => context.isAccountSettingsVisible, onUnavailable: 'skip'
  },
  {
    id: 'manual-help', route: '/dashboard', selector: '[data-tour="help-center-actions"]',
    titleKey: 'TOUR.MANUAL.HELP.TITLE', descriptionKey: 'TOUR.MANUAL.HELP.DESCRIPTION',
    side: 'left', openAction: 'header.open-help', closeAction: 'header.open-help',
    requires: canRead('company.dashboard.read'), onUnavailable: 'skip'
  }
];

@Injectable({ providedIn: 'root' })
export class TourCatalogService {
  build(kind: TourKind, context: TourContext): TourStepDefinition[] {
    const source = kind === 'day-one' ? DAY_ONE_STEPS : MANUAL_STEPS;
    return source.filter((step) => !step.requires || step.requires(context));
  }
}
